import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  firstValueFrom,
  map,
  Observable,
  retry,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import {
  SupplyAccountingAccountDto,
  SupplyClassificationRef,
  SupplyCompanyAccountDto,
  SupplyDto,
  SupplyFilterOption,
  SupplyFilterSelects,
  SupplyFormSelects,
  SupplyListQuery,
  SupplyListResult,
  SupplyRelationRef,
  SupplyRequest,
} from './supplies-api.types';
import { mapSettingsError, SettingsApiError } from './settings-api.errors';
import { SETTINGS_API_URL } from './settings-api.tokens';

const REQUEST_TIMEOUT_MS = 15_000;
const LIST_PAGE_SIZE = 100;
const SUPPLIES_PATH = '/catalogs/supplies/supplies';

@Injectable({ providedIn: 'root' })
export class SuppliesApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(SETTINGS_API_URL).replace(/\/+$/, '')}${SUPPLIES_PATH}`;

  list(query: SupplyListQuery = {}): Observable<SupplyListResult> {
    return this.http
      .get<unknown>(this.baseUrl, { params: this.listParams(query) })
      .pipe(
        timeout(REQUEST_TIMEOUT_MS),
        retry({ count: 1, delay: error => this.retryDelay(error) }),
        map(body => unwrapList(body)),
        catchError(error => throwError(() => mapSettingsError(error))),
      );
  }

  formSelects(): Promise<SupplyFormSelects> {
    return this.request(
      this.http.get<unknown>(`${this.baseUrl}/form-selects`),
      body => unwrapFormSelects(body),
    );
  }

  accountingAccounts(companyUuid: string): Promise<SupplyAccountingAccountDto[]> {
    const params = new HttpParams().set('companyUuid', companyUuid);
    return this.request(
      this.http
        .get<unknown>(`${this.baseUrl}/form-selects/accounting-accounts`, { params })
        .pipe(
          timeout(REQUEST_TIMEOUT_MS),
          retry({ count: 1, delay: error => this.retryDelay(error) }),
        ),
      unwrapAccountingAccounts,
    );
  }

  filterSelects(): Promise<SupplyFilterSelects> {
    return this.request(
      this.http.get<unknown>(`${this.baseUrl}/filter-selects`),
      body => unwrapFilterSelects(body),
    );
  }

  getById(uuid: string): Promise<SupplyDto> {
    return this.request(
      this.http.get<unknown>(`${this.baseUrl}/${uuid}`),
      body => unwrapItem(body),
    );
  }

  create(payload: SupplyRequest): Promise<SupplyDto> {
    return this.request(
      this.http.post<unknown>(this.baseUrl, payload),
      body => unwrapItem(body),
    );
  }

  update(uuid: string, payload: SupplyRequest): Promise<SupplyDto> {
    return this.request(
      this.http.put<unknown>(`${this.baseUrl}/${uuid}`, payload),
      body => unwrapItem(body),
    );
  }

  activate(uuid: string): Promise<void> {
    return this.request(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/activate`, null),
      () => undefined,
    );
  }

  inactivate(uuid: string): Promise<void> {
    return this.request(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/inactivate`, null),
      () => undefined,
    );
  }

  remove(uuid: string): Promise<void> {
    return this.request(
      this.http.delete<unknown>(`${this.baseUrl}/${uuid}`),
      () => undefined,
    );
  }

  private async request<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Promise<T> {
    try {
      const body = await firstValueFrom(source.pipe(timeout(REQUEST_TIMEOUT_MS)));
      return project(body);
    } catch (error) {
      throw mapSettingsError(error);
    }
  }

  private listParams(query: SupplyListQuery): HttpParams {
    let params = new HttpParams()
      .set('page', String(query.page ?? 0))
      .set('size', String(query.size ?? LIST_PAGE_SIZE));

    if (query.code) params = params.set('filters[code]', query.code);
    if (query.name) params = params.set('filters[name]', query.name);
    if (query.description) params = params.set('filters[description]', query.description);
    if (query.classification) {
      params = params.set('filters[classification]', query.classification);
    }
    if (query.typeUuids?.length) {
      params = params.set('select[type]', query.typeUuids.join(','));
    }
    if (query.groupUuids?.length) {
      params = params.set('select[group]', query.groupUuids.join(','));
    }
    if (query.measurementUnitUuids?.length) {
      params = params.set('select[measurementUnit]', query.measurementUnitUuids.join(','));
    }
    if (query.status?.length) {
      params = params.set('select[status]', query.status.join(','));
    }
    if (query.sortBy) params = params.set('sortBy', query.sortBy);
    if (query.order) params = params.set('order', query.order);

    return params;
  }

  private retryDelay(error: unknown): Observable<number> {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 429) {
        const retryAfter = Number(error.headers.get('Retry-After'));
        const seconds =
          Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1;
        return timer(seconds * 1000);
      }
      if (error.status >= 500 || error.status === 0) {
        return timer(300);
      }
    }
    return throwError(() => error);
  }
}

function unwrapData(body: unknown): unknown {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: unknown }).data;
  }
  return body;
}

function unwrapList(body: unknown): SupplyListResult {
  const data = unwrapData(body);
  if (Array.isArray(data)) {
    return { items: data.map(normalizeListItem), total: data.length };
  }

  if (data && typeof data === 'object') {
    const page = data as {
      data?: unknown;
      content?: unknown;
      items?: unknown;
      pagination?: { totalRecords?: unknown };
      totalElements?: unknown;
      total?: unknown;
    };
    const nestedItems = page.data;
    if (Array.isArray(nestedItems)) {
      const totalRecords = page.pagination?.totalRecords;
      return {
        items: nestedItems.map(normalizeListItem),
        total: typeof totalRecords === 'number' ? totalRecords : nestedItems.length,
      };
    }
    if (Array.isArray(page.content)) {
      const items = page.content.map(normalizeListItem);
      return {
        items,
        total:
          typeof page.totalElements === 'number' ? page.totalElements : items.length,
      };
    }
    if (Array.isArray(page.items)) {
      const items = page.items.map(normalizeListItem);
      return {
        items,
        total: typeof page.total === 'number' ? page.total : items.length,
      };
    }
  }

  throw new SettingsApiError('unexpected', 0);
}

function normalizeListItem(raw: unknown): SupplyDto {
  const record = asRecord(raw);
  if (!record) throw new SettingsApiError('unexpected', 0);
  return normalizeItem(record);
}

function unwrapItem(body: unknown): SupplyDto {
  const data = unwrapData(body);
  if (!data || typeof data !== 'object') {
    throw new SettingsApiError('unexpected', 0);
  }

  const item = normalizeItem(data as Record<string, unknown>);
  if (!item.uuid || typeof item.code !== 'string' || typeof item.name !== 'string') {
    throw new SettingsApiError('unexpected', 0);
  }
  return item;
}

function normalizeItem(raw: Record<string, unknown>): SupplyDto {
  const type = normalizeRelationRef(raw['type']);
  const group = normalizeRelationRef(raw['group']);
  const measurementUnit = normalizeRelationRef(raw['measurementUnit']);
  const companyAccounts = normalizeCompanyAccounts(raw['companyAccounts']);
  return {
    uuid: String(raw['uuid'] ?? ''),
    code: String(raw['code'] ?? ''),
    name: String(raw['name'] ?? ''),
    description: typeof raw['description'] === 'string' ? raw['description'] : null,
    classification: normalizeClassification(raw['classification']),
    type,
    group,
    measurementUnit,
    typeUuid: typeof raw['typeUuid'] === 'string' ? raw['typeUuid'] : type?.uuid,
    groupUuid: typeof raw['groupUuid'] === 'string' ? raw['groupUuid'] : group?.uuid,
    measurementUnitUuid:
      typeof raw['measurementUnitUuid'] === 'string'
        ? raw['measurementUnitUuid']
        : measurementUnit?.uuid,
    ...(companyAccounts ? { companyAccounts } : {}),
    status: raw['status'] as SupplyDto['status'],
    enabled: typeof raw['enabled'] === 'boolean' ? raw['enabled'] : undefined,
    createdAt: typeof raw['createdAt'] === 'string' ? raw['createdAt'] : undefined,
    updatedAt: typeof raw['updatedAt'] === 'string' ? raw['updatedAt'] : undefined,
  };
}

function unwrapAccountingAccounts(body: unknown): SupplyAccountingAccountDto[] {
  const data = unwrapData(body);
  if (!Array.isArray(data)) throw new SettingsApiError('unexpected', 0);
  return data
    .map(normalizeAccountingAccount)
    .filter((item): item is SupplyAccountingAccountDto => !!item);
}

function normalizeCompanyAccounts(
  raw: unknown,
): SupplyCompanyAccountDto[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw.flatMap(item => {
    const record = asRecord(item);
    if (!record) return [];
    const company = normalizeRelationRef(record['company']);
    if (!company) return [];
    const account = record['accountingAccount'];
    return [
      {
        company,
        accountingAccount: account == null ? null : normalizeAccountingAccount(account),
      },
    ];
  });
}

function normalizeAccountingAccount(raw: unknown): SupplyAccountingAccountDto | null {
  const record = asRecord(raw);
  if (!record) return null;
  const uuid = stringField(record, ['uuid']);
  if (!uuid) return null;
  const accountNumber = stringField(record, ['accountNumber']) ?? '';
  return {
    uuid,
    name: stringField(record, ['name']) ?? '',
    accountNumber,
    formattedAccountNumber:
      stringField(record, ['formattedAccountNumber']) ?? accountNumber,
    source: normalizeRelationRef(record['source']),
    type: normalizeRelationRef(record['type']),
    affectable: record['affectable'] !== false,
    selectable: record['selectable'] !== false,
  };
}

function unwrapFormSelects(body: unknown): SupplyFormSelects {
  const data = asRecord(unwrapData(body)) ?? asRecord(body);
  if (!data) throw new SettingsApiError('unexpected', 0);

  return {
    classifications: firstArray(data, ['classifications', 'classification'])
      .map(normalizeClassification)
      .filter((item): item is SupplyClassificationRef => !!item),
    types: firstArray(data, ['types', 'type'])
      .map(normalizeRelationRef)
      .filter((item): item is SupplyRelationRef => !!item),
    groups: firstArray(data, ['groups', 'group'])
      .map(normalizeRelationRef)
      .filter((item): item is SupplyRelationRef => !!item),
    measurementUnits: firstArray(data, ['measurementUnits', 'measurementUnit'])
      .map(normalizeRelationRef)
      .filter((item): item is SupplyRelationRef => !!item),
  };
}

function unwrapFilterSelects(body: unknown): SupplyFilterSelects {
  const data = asRecord(unwrapData(body)) ?? asRecord(body);
  if (!data) throw new SettingsApiError('unexpected', 0);

  return {
    status: firstArray(data, ['status', 'statuses']).map(toFilterOption).filter(isFilterOption),
    types: firstArray(data, ['type', 'types']).map(toFilterOption).filter(isFilterOption),
    groups: firstArray(data, ['group', 'groups']).map(toFilterOption).filter(isFilterOption),
  };
}

function normalizeClassification(raw: unknown): SupplyClassificationRef | null {
  if (typeof raw === 'string' && raw.trim()) {
    const value = raw.trim();
    return {
      uuid: value,
      name: classificationLabel(value),
      ...(isClassificationCode(value) ? { code: value } : {}),
    };
  }
  const record = asRecord(raw);
  if (!record) return null;
  const value = stringField(record, ['uuid', 'id', 'value']) ?? '';
  const rawCode = stringField(record, ['code', 'value']) ?? '';
  const name = stringField(record, ['name', 'label']) ?? '';
  if (!value && !name) return null;
  return {
    uuid: value || name,
    name: name || classificationLabel(rawCode || value),
    ...(isClassificationCode(rawCode) ? { code: rawCode } : {}),
  };
}

function classificationLabel(value: string): string {
  const normalized = value.trim().toUpperCase();
  if (normalized === 'PRODUCT') return 'Producto';
  if (normalized === 'SERVICE') return 'Servicio';
  return value;
}

function isClassificationCode(value: string): value is 'PRODUCT' | 'SERVICE' {
  return value === 'PRODUCT' || value === 'SERVICE';
}

function normalizeRelationRef(raw: unknown): SupplyRelationRef | null {
  const record = asRecord(raw);
  if (!record) return null;
  const uuid = stringField(record, ['uuid', 'id', 'value']);
  const name = stringField(record, ['name', 'label']);
  if (!uuid || !name) return null;
  return { uuid, name };
}

function toFilterOption(raw: unknown): SupplyFilterOption | null {
  if (typeof raw === 'string' && raw.trim()) {
    return { name: raw, value: raw };
  }
  const record = asRecord(raw);
  if (!record) return null;
  const name = stringField(record, ['name', 'label']) ?? '';
  const value = stringField(record, ['value', 'uuid', 'id', 'code']) ?? name;
  if (!name && !value) return null;
  return { name: name || value, value: value || name };
}

function isFilterOption(option: SupplyFilterOption | null): option is SupplyFilterOption {
  return option !== null;
}

function firstArray(record: Record<string, unknown>, keys: string[]): unknown[] {
  for (const key of keys) {
    const value = record[key];
    if (Array.isArray(value)) return value;
  }
  return [];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function stringField(record: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return null;
}
